import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/seo/SEOHead';

const AntipodismePage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const antipodismeSchema = [
        {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Antipodisme: De Kunstfilosofie van Clemens Briels",
            "description": "Ontdek het Antipodisme, de unieke kunststijl van Clemens Briels. Een explosie van kleur, contrast en optimisme, geboren uit Latijns-Amerikaanse en COBRA-invloeden.",
            "author": { "@type": "Person", "name": "Clemens Briels" },
            "publisher": { "@type": "Organization", "name": "Kunstlounge", "url": "https://kunstlounge.nl/" },
            "url": "https://kunstlounge.nl/antipodisme",
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://kunstlounge.nl/" },
                    { "@type": "ListItem", "position": 2, "name": "Antipodisme", "item": "https://kunstlounge.nl/antipodisme" }
                ]
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "Wat is het Antipodisme?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Het Antipodisme is een kunststijl gecreëerd door de Nederlandse kunstenaar Clemens Briels (1946), waarbij tegenpolen worden gecombineerd tot een dynamisch, kleurrijk en optimistisch beeld. Kenmerkend zijn explosieve kleuropposities, expressieve figuren en humoristische composities over het menselijk bestaan."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Waar komt de term Antipodisme vandaan?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Het woord antipode verwijst naar twee punten die lijnrecht tegenover elkaar liggen op een bol. Clemens Briels formuleerde de term zelf om zijn kunstopvatting te beschrijven: ernst en humor, intieme emotie en monumentale schaal, eenvoud en complexiteit die elkaar niet bestrijden maar versterken."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Wat is de invloed van CoBrA op het Antipodisme van Clemens Briels?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "De CoBrA-beweging (1948-1951) liet zien dat Europese kunstenaars buiten de academische kaders konden werken: expressief en intuïtief, zonder hiërarchie tussen figuur en achtergrond. Clemens Briels deelt die vrijheid, maar voegt er een laag narratief en humor aan toe die bij CoBrA doorgaans ontbreekt."
                    }
                }
            ]
        }
    ];

    return (
        <main id="main-content" className="pt-20">
            <SEOHead
                title="Antipodisme: kunststijl van Clemens Briels | Kunstlounge"
                description="Antipodisme is de unieke kunststijl van Clemens Briels: explosief kleurgebruik, figuratieve composities en optimisme als filosofisch uitgangspunt. Bekijk originele werken."
                canonical="/antipodisme/"
                imageUrl="/images/collectie/you-carved-out-your-name/you-carved-out-your-name-1.webp"
                schemaData={antipodismeSchema}
                hreflangEn="/en/antipodism.html"
                hreflangNl="/antipodisme/"
            />
            {/* Hero Section */}
            <section className="relative py-32 bg-white overflow-hidden">
                <div className="container mx-auto px-6">
                    <span className="block text-accent text-xs tracking-[0.4em] uppercase mb-8">
                        Artistieke Filosofie
                    </span>
                    <h1 className="text-6xl md:text-8xl font-serif text-primary mb-10 leading-tight">
                        De Wereld op z'n Kop:<br />
                        <span className="text-gray-300 font-light italic">Antipodisme</span>
                    </h1>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6 max-w-4xl">
                    <div className="prose prose-lg max-w-none font-light text-gray-600 leading-relaxed">
                        <p className="text-2xl font-serif text-primary mb-12 leading-relaxed">
                            Antipodisme is geen stijlnaam die Briels van buitenaf kreeg toegewezen. Het is een begrip dat hij zelf formuleerde om te beschrijven wat hij al jaren deed: twee uitersten samenvoegen tot een geheel dat groter is dan de afzonderlijke delen.
                        </p>

                        <h2 className="text-3xl font-serif text-primary mt-16 mb-8 underline decoration-accent decoration-1 underline-offset-8">Oorsprong en Betekenis</h2>
                        <p className="mb-8">
                            Het woord antipode verwijst naar twee punten die lijnrecht tegenover elkaar liggen op een bol. Briels gebruikt dat beeld voor zijn kunstopvatting: ernst en humor, intieme emotie en monumentale schaal, eenvoud en complexiteit. Die combinaties strijden niet in zijn werk, ze versterken elkaar.
                        </p>
                        <p className="mb-8">
                            Briels introduceerde het Antipodisme als eigen stroming in zijn volwassen carrière, nadat hij een figuratieve basis had opgebouwd in Nederland. De stijl is onmiskenbaar: expressieve menselijke figuren, kleurpaletten die niet de werkelijkheid kopiëren maar een emotionele logica volgen, composities die meerdere verhalen tegelijk vertellen. Elk werk nodigt uit tot herlezen.
                        </p>

                        <div className="my-20 p-12 bg-gray-50 border-l-4 border-accent italic shadow-sm">
                            Het Antipodisme vraagt geen kunsthistorische kennis van de kijker. Wat het vraagt is aanwezig zijn voor het werk.
                        </div>

                        <h2 className="text-3xl font-serif text-primary mt-16 mb-8 underline decoration-accent decoration-1 underline-offset-8">Invloeden: Van COBRA tot de Cariben</h2>
                        <p className="mb-8">
                            De COBRA-beweging (1948–1951) toonde dat Europese kunstenaars buiten de academische kaders konden werken: expressief, intuïtief, zonder hiërarchie tussen figuur en achtergrond. Briels deelt die vrijheid, maar voegde er een laag van narratief en humor aan toe die COBRA doorgaans mist. Zijn figuren zijn geen spontane vormen, ze dragen rollen, houdingen, een standpunt.
                        </p>
                        <p className="mb-8">
                            De Latijns-Amerikaanse invloed is concreet en documenteerbaar. Briels werkte en exposeerde in de Cariben en Latijns-Amerika, en dat is terug te zien in zijn kleurgebruik en compositieopbouw. De zeefdruk <em>Antigua, a bay a day</em> uit 2000 (editie 132 uit 175 stuks, 95 bij 110 centimeter) is daarvan een direct bewijs: tropische kleuren, een ritmische compositie, de sfeer van een eiland dat elke dag een nieuw gezicht toont. In 2008 volgde een museumtentoonstelling in Shanghai. Zijn werk exposeerde op vijf continenten.
                        </p>

                        <h2 className="text-3xl font-serif text-primary mt-16 mb-8 underline decoration-accent decoration-1 underline-offset-8">Media en Techniek</h2>
                        <p className="mb-8">
                            Briels werkt in meerdere media, en dat is geen artistiek statement maar een praktische keuze: elk medium legt een andere eigenschap van zijn beeldtaal bloot.
                        </p>
                        <p className="mb-8">
                            Bij zijn zeefdrukken worden kleuren laag voor laag aangebracht, elke kleur een aparte drukgang. In de collectie is dat goed zichtbaar: <em>Algunos Gentes Hacen la Comedia</em> (kleurenzeefdruk 97/175, 104 bij 155 cm, 2005) heeft een kleurdiepte die je alleen begrijpt als je het werk van dichtbij bekijkt. De lagen zijn niet perfect op elkaar uitgelijnd; die lichte verschuiving is onderdeel van het procédé.
                        </p>
                        <p className="mb-8">
                            Het 3D wandobject <em>Yellow Beard of Vegas</em> (acryl op hars, 49 bij 49 bij 7 cm, 2000, editie 4/50) laat een andere kant zien. Het hars geeft een reflectie die op doek onmogelijk is, en de 7 centimeter diepte maakt van het werk een aanwezigheid in de ruimte in plaats van een vlak beeld.
                        </p>

                        <p className="text-xl font-serif text-primary mt-20">
                            Een Briels in huis is een werk dat je anders ziet naargelang het licht, de tijd van de dag en je eigen stemming. Dat is precies wat hij beoogt.
                        </p>
                    </div>

                    {/* Werken uit de collectie als Antipodisme-illustraties */}
                    <div className="mt-24 pt-20 border-t border-gray-100">
                        <h2 className="text-3xl font-serif text-primary mb-4">Antipodisme in de Collectie</h2>
                        <p className="text-gray-500 font-light mb-16 max-w-2xl">
                            Elk werk in onze collectie is een levend voorbeeld van de Antipodisme-filosofie. Hieronder drie werken en wat ze belichamen.
                        </p>

                        <div className="space-y-16">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                                <Link to="/artwork/you-carved-out-your-name" className="block overflow-hidden bg-gray-50 shadow-lg hover:shadow-xl transition-shadow duration-500 group">
                                    <img
                                        src="/images/collectie/you-carved-out-your-name/you-carved-out-your-name.jpg"
                                        alt="You carved out your name in my heart van Clemens Briels"
                                        className="w-full object-cover group-hover:scale-102 transition-transform duration-700"
                                        style={{ maxHeight: '360px', objectFit: 'contain' }}
                                    />
                                </Link>
                                <div>
                                    <span className="text-accent text-xs tracking-[0.3em] uppercase mb-4 block">Tegenstelling: Kwetsbaarheid vs. Kracht</span>
                                    <h3 className="text-2xl font-serif text-primary mb-4">You Carved Out Your Name</h3>
                                    <p className="text-gray-500 font-light leading-relaxed mb-6">
                                        Antipodisme draait om contrasten die elkaar versterken. In dit werk botst de intimiteit van een persoonlijk gevoel, liefde die zich inkerft, op de monumentale schaal van het doek (130x100cm). Het resultaat is rauw en optimistisch tegelijk.
                                    </p>
                                    <Link to="/artwork/you-carved-out-your-name" className="text-[10px] uppercase tracking-widest border-b border-primary pb-2 hover:text-accent hover:border-accent transition-colors">
                                        Bekijk dit werk
                                    </Link>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                                <div className="order-2 md:order-1">
                                    <span className="text-accent text-xs tracking-[0.3em] uppercase mb-4 block">Tegenstelling: Ernst vs. Feest</span>
                                    <h3 className="text-2xl font-serif text-primary mb-4">Algunos Gentes Hacen la Comedia</h3>
                                    <p className="text-gray-500 font-light leading-relaxed mb-6">
                                        Het Antipodisme verklaart dat mensen nooit volledig te vatten zijn. "Sommigen spelen komedie, anderen niet". Briels toont dit in een panoramische zeefdruk van 104x155cm, vol figuren die elk hun eigen werkelijkheid bewonen. Typisch Briels: humor als filosofisch instrument.
                                    </p>
                                    <Link to="/artwork/algunos-gentes-hacen-la-comedia" className="text-[10px] uppercase tracking-widest border-b border-primary pb-2 hover:text-accent hover:border-accent transition-colors">
                                        Bekijk dit werk
                                    </Link>
                                </div>
                                <Link to="/artwork/algunos-gentes-hacen-la-comedia" className="block order-1 md:order-2 overflow-hidden bg-gray-50 shadow-lg hover:shadow-xl transition-shadow duration-500 group">
                                    <img
                                        src="/images/collectie/algunos-gentes-hacen-la-comedia/algunos-gentes-hacen-la-comedia-otros-no-1.webp"
                                        alt="Algunos Gentes Hacen la Comedia van Clemens Briels"
                                        className="w-full object-cover group-hover:scale-102 transition-transform duration-700"
                                        style={{ maxHeight: '360px', objectFit: 'contain' }}
                                    />
                                </Link>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                                <Link to="/artwork/antigua-a-bay-a-day" className="block overflow-hidden bg-gray-50 shadow-lg hover:shadow-xl transition-shadow duration-500 group">
                                    <img
                                        src="/images/collectie/antigua-a-bay-a-day/antigua-a-bay-a-day-1.jpg"
                                        alt="Antigua a Bay a Day van Clemens Briels"
                                        className="w-full object-cover group-hover:scale-102 transition-transform duration-700"
                                        style={{ maxHeight: '360px', objectFit: 'contain' }}
                                    />
                                </Link>
                                <div>
                                    <span className="text-accent text-xs tracking-[0.3em] uppercase mb-4 block">Tegenstelling: Beweging vs. Stilte</span>
                                    <h3 className="text-2xl font-serif text-primary mb-4">Antigua, a Bay a Day</h3>
                                    <p className="text-gray-500 font-light leading-relaxed mb-6">
                                        De Cariben als inspiratiebron: ritme, kleur, de spanning tussen het bruisende leven en de stilte van de zee. Briels vangt dit in een zeefdruk met Latijns-Amerikaans gevoel. De compositie leest als muziek, elk element in dialoog met het volgende.
                                    </p>
                                    <Link to="/artwork/antigua-a-bay-a-day" className="text-[10px] uppercase tracking-widest border-b border-primary pb-2 hover:text-accent hover:border-accent transition-colors">
                                        Bekijk dit werk
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* FAQ */}
                    <div className="mt-24 pt-20 border-t border-gray-100">
                        <h2 className="text-3xl font-serif text-primary mb-12">Veelgestelde vragen over het Antipodisme</h2>
                        <div className="space-y-8">
                            {[
                                {
                                    q: 'Wat is het Antipodisme?',
                                    a: 'Het Antipodisme is een kunststijl gecreëerd door de Nederlandse kunstenaar Clemens Briels (1946), waarbij tegenpolen worden gecombineerd tot een dynamisch, kleurrijk en optimistisch beeld. Kenmerkend zijn explosieve kleuropposities, expressieve figuren en humoristische composities over het menselijk bestaan.'
                                },
                                {
                                    q: 'Waar komt de term Antipodisme vandaan?',
                                    a: 'Het woord antipode verwijst naar twee punten die lijnrecht tegenover elkaar liggen op een bol. Clemens Briels formuleerde de term zelf om zijn kunstopvatting te beschrijven: ernst en humor, intieme emotie en monumentale schaal, eenvoud en complexiteit die elkaar niet bestrijden maar versterken.'
                                },
                                {
                                    q: 'Wat is de invloed van CoBrA op het Antipodisme van Clemens Briels?',
                                    a: 'De CoBrA-beweging (1948-1951) liet zien dat Europese kunstenaars buiten de academische kaders konden werken: expressief en intuïtief, zonder hiërarchie tussen figuur en achtergrond. Clemens Briels deelt die vrijheid, maar voegt er een laag narratief en humor aan toe die bij CoBrA doorgaans ontbreekt.'
                                },
                            ].map(({ q, a }) => (
                                <div key={q} className="border-b border-gray-100 pb-8">
                                    <h3 className="font-serif text-primary text-xl mb-3">{q}</h3>
                                    <p className="text-gray-500 font-light leading-relaxed">{a}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-20 flex justify-center">
                        <Link
                            to="/#collectie"
                            className="inline-block px-12 py-5 bg-primary text-white hover:bg-accent transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold shadow-xl"
                        >
                            Bekijk alle Werken
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default AntipodismePage;
